import { useEffect } from 'react';
import { useAuth, useAuthActions, ContextHolder } from '@frontegg/react';
import {
  setFronteggAccessToken,
  setFronteggLogoutFn,
  setFronteggRefreshFn,
} from '../app/http-common';

/**
 * Invisible bridge component that syncs the Frontegg access token
 * into the non-React http-common module so axios requests carry the
 * correct Authorization header.
 *
 * Must be rendered inside <FronteggProvider>.
 */
const FronteggTokenSync = () => {
  const { user, isAuthenticated } = useAuth();
  const authActions = useAuthActions();

  // Push the latest access token into http-common
  useEffect(() => {
    if (isAuthenticated && user?.accessToken) {
      setFronteggAccessToken(user.accessToken);
    } else {
      setFronteggAccessToken(null);
    }
  }, [isAuthenticated, user?.accessToken]);

  // Provide the logout function using Frontegg OAuth logout redirect
  useEffect(() => {
    const logout = () => {
      const baseUrl = ContextHolder.getContext().baseUrl;
      window.location.href = `${baseUrl}/oauth/logout?post_logout_redirect_uri=${window.location.origin}`;
    };
    setFronteggLogoutFn(logout);
  }, []);

  // Provide a silent token-refresh function so http-common can retry 401s
  // before forcing logout. Prefer refreshToken() if available, fall back to
  // requestAuthorize(true) which the Frontegg SDK uses for silent reauth.
  useEffect(() => {
    const refresh = () => {
      if (typeof authActions?.refreshToken === 'function') {
        return authActions.refreshToken();
      }
      if (typeof authActions?.requestAuthorize === 'function') {
        return authActions.requestAuthorize(true);
      }
      return undefined;
    };
    setFronteggRefreshFn(refresh);
  }, [authActions]);

  return null; // renders nothing
};

export default FronteggTokenSync;
